import { Component, OnInit } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { Store } from '@ngrx/store'
import { AppState } from '../../store/state/app.state'
import { EmployeeService } from '../../services/employee/employee.service'
import { getEmployeeFromState } from '../../store/selector/employee.selector'
import { DatePipe } from '@angular/common'
import { OptionsService } from '../../services/common/options.service'
import {
  AddressAPIModel,
  CodeLookup,
  EmploymentBasicDetailsAPIModel,
  StatutoryDetailsAPIModel
} from '../../models/api.model'
import { GlobalConstants } from '../../constants/global-constants'
import { Observable, Subscription } from 'rxjs'
import { EmployeeState } from '../../store/state/employee.state'
import {
  countryCodeValidator,
  dateOfBirthValidator,
  emailValidator,
  emailValidatorForNonMandatory
} from '../../validators/validators.validator'
import countryCodes from '../../../assets/other/country-code.json'

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss']
})
export class ProfileComponent implements OnInit {
  employeeBasicDataForm: FormGroup
  employeeContactDataForm: FormGroup
  employmentBasicDataForm: FormGroup
  statutoryDetailsForm: FormGroup
  maritalStatuses: CodeLookup[]
  genders: CodeLookup[]
  nationalities: CodeLookup[]
  cities: CodeLookup[]
  states: CodeLookup[]
  departments: CodeLookup[]
  branches: CodeLookup[]
  categories: CodeLookup[]
  locations: CodeLookup[]
  responses: CodeLookup[]
  relationships: CodeLookup[]
  employeeData$: Observable<EmployeeState>
  employeeDataSubscription: Subscription
  initialBasicData: any
  initialContactData: any
  initialEmploymentBasicData: any
  initialStatutoryDetails: any
  today: string
  countryCodes: { dialCode: string }[] = countryCodes
  countries: { name: string }[] = countryCodes

  readonly HAVE_PF_ACC_CONTROL_NAME = 'havePfAccount'
  readonly PF_NUMBER_CONTROL_NAME = 'pfNumber'
  readonly UAN_NUMBER_CONTROL_NAME = 'uanNumber'

  /*readonly HAVE_ESI_ACC_CONTROL_NAME = 'haveEsiAccount'
  readonly ESI_NUMBER_CONTROL_NAME = 'esiNumber'
  readonly HAVE_LWF_ACC_CONTROL_NAME = 'lwfApplicability'
  readonly LWF_NUMBER_CONTROL_NAME = 'lwfNumber'*/

  constructor(
    private formBuilder: FormBuilder,
    private employeeService: EmployeeService,
    private appState: Store<AppState>,
    private datePipe: DatePipe,
    private optionsService: OptionsService
  ) {
    this.today = this.datePipe.transform(new Date(), 'yyyy-MM-dd', GlobalConstants.IST_OFFSET)
  }

  ngOnInit(): void {
    this.populateOptions()
    this.buildEmployeeEmploymentForms()
  }

  get basicData_f() {
    return this.employeeBasicDataForm.controls
  }

  get contactData_f() {
    return this.employeeContactDataForm.controls
  }

  get employmentBasicData_f() {
    return this.employmentBasicDataForm.controls
  }

  get statutoryData_f() {
    return this.statutoryDetailsForm.controls
  }

  updateEmployeeBasicData() {
    if (this.employeeBasicDataForm.valid) {
      this.employeeService.dispatchUpdateEmployeePersonalInfoAction(this.employeeBasicDataForm.getRawValue())
      this.initialBasicData = this.employeeBasicDataForm.getRawValue()
    }
  }

  updateEmployeeContact() {
    if (this.employeeContactDataForm.valid) {
      let payload = this.buildEmployeePayloadForContactData(this.employeeContactDataForm.getRawValue())
      this.employeeService.dispatchUpdateEmployeePersonalInfoAction(payload)
      this.initialContactData = this.employeeContactDataForm.getRawValue()
    }
  }

  updateEmploymentBasicData() {
    if (this.employmentBasicDataForm.valid) {
      let payload = this.buildEmployeePayloadForEmploymentBasicData(this.employmentBasicDataForm.getRawValue())
      this.employeeService.dispatchUpdateEmployeePersonalInfoAction(payload)
      this.initialEmploymentBasicData = this.employmentBasicDataForm.getRawValue()
    }
  }

  updateStatutoryDetails() {
    if (this.statutoryDetailsForm.valid) {
      let payload = this.buildEmployeePayloadForStatutoryDetails(this.statutoryDetailsForm.getRawValue())
      this.employeeService.dispatchUpdateEmployeePersonalInfoAction(payload)
      this.initialStatutoryDetails = this.statutoryDetailsForm.getRawValue()
    }
  }

  buildEmployeeEmploymentForms() {
    this.employeeService.dispatchGetEmployeePersonalInfoData()
    this.employeeData$ = this.appState.select(getEmployeeFromState)

    this.employeeDataSubscription = this.employeeData$.subscribe((employee) => {
      this.employeeBasicDataForm = this.formBuilder.group({
        name: [
          {
            value: employee ? `${employee.firstName || ''} ${employee.lastName || ''}`.trim() : '',
            disabled: true
          },
          [Validators.required, Validators.minLength(3), Validators.maxLength(150), Validators.pattern('^[a-zA-Z ]*$')]
        ],
        employeeCode: [
          { value: employee?.employeeCode ? employee.employeeCode : '', disabled: true },
          Validators.required
        ],
        companyEntityId: [employee?.companyEntityId ? employee.companyEntityId : '', Validators.required],
        doj: [
          {
            value: employee?.doj
              ? this.datePipe.transform(new Date(employee.doj), 'yyyy-MM-dd', GlobalConstants.IST_OFFSET)
              : '',
            disabled: true
          },
          Validators.required
        ],
        dob: [
          {
            value: employee?.dob
              ? this.datePipe.transform(new Date(employee.dob), 'yyyy-MM-dd', GlobalConstants.IST_OFFSET)
              : '',
            disabled: true
          },
          [(Validators.required, dateOfBirthValidator(GlobalConstants.employeeMinAge))]
        ],
        maritalStatus: [employee?.maritalStatus ? employee.maritalStatus : '', Validators.required],
        gender: [{ value: employee?.gender ? employee.gender : '', disabled: true }, Validators.required],
        emailId: [
          { value: employee?.emailId ? employee.emailId : '', disabled: true },
          [Validators.required, Validators.minLength(5), Validators.maxLength(50), emailValidator()]
        ],
        nationality: [{ value: employee?.nationality ? employee.nationality : '', disabled: true }],
        address: [{ value: employee?.address ? employee?.address : null, disabled: true }]
      })

      this.initialBasicData = { ...this.employeeBasicDataForm.getRawValue() }

      this.employeeContactDataForm = this.formBuilder.group({
        secondaryEmailId: [
          employee?.secondaryEmailId ? employee.secondaryEmailId : '',
          [Validators.minLength(5), Validators.maxLength(50), emailValidatorForNonMandatory()]
        ],
        primaryMobileNumber: [
          employee?.mobileNumber?.includes('-') ? employee.mobileNumber.split('-')[1] : employee?.mobileNumber || '',
          [Validators.pattern('^[0-9]{10}$')]
        ],
        primaryCountryCode: [
          employee?.mobileNumber?.includes('-') ? employee.mobileNumber.split('-')[0] : '+91',
          [countryCodeValidator()]
        ],
        secondaryCountryCode: [
          employee?.alternateMobileNumber?.includes('-') ? employee.alternateMobileNumber.split('-')[0] : '+91',
          [countryCodeValidator()]
        ],
        secondaryMobileNumber: [
          employee?.alternateMobileNumber?.includes('-')
            ? employee.alternateMobileNumber.split('-')[1]
            : employee?.alternateMobileNumber || '',
          [Validators.pattern('^[0-9]{10}$')]
        ],
        country: [employee?.address?.country ? employee.address.country : 'India'],
        state: [employee?.address?.state ? employee.address.state : ''],
        doorNo: [
          employee?.address?.address1 ? employee.address.address1 : '',
          [Validators.minLength(1), Validators.maxLength(10)]
        ],
        streetName: [
          employee?.address?.address2 ? employee.address.address2 : '',
          [Validators.minLength(3), Validators.maxLength(25)]
        ],
        areaName: [
          employee?.address?.address3 ? employee.address.address3 : '',
          [Validators.minLength(3), Validators.maxLength(25)]
        ],
        city: [
          employee?.address?.city ? employee.address.city : '',
          [Validators.minLength(3), Validators.maxLength(25)]
        ],
        pinCode: [
          employee?.address?.pin ? employee.address.pin : '',
          [Validators.minLength(5), Validators.maxLength(10), Validators.pattern('^(?!0+$)[0-9]+$')]
        ],
        address: [employee?.address ? employee.address : null]
      })

      this.initialContactData = { ...this.employeeContactDataForm.getRawValue() }

      this.employmentBasicDataForm = this.formBuilder.group({
        departmentName: [
          {
            value: employee?.employeeEmploymentBasicDetails?.departmentName
              ? employee.employeeEmploymentBasicDetails.departmentName
              : '',
            disabled: true
          }
        ],
        branch: [
          {
            value: employee?.employeeEmploymentBasicDetails?.branch
              ? employee.employeeEmploymentBasicDetails.branch
              : '',
            disabled: true
          }
        ],
        designation: [
          {
            value: employee?.employeeEmploymentBasicDetails?.designation
              ? employee.employeeEmploymentBasicDetails.designation
              : '',
            disabled: true
          }
          /*[Validators.minLength(5), Validators.maxLength(30), Validators.pattern('^[a-zA-Z ]*$')]*/
        ],
        category: [
          {
            value: employee?.employeeEmploymentBasicDetails?.category
              ? employee.employeeEmploymentBasicDetails.category
              : '',
            disabled: true
          }
        ],
        location: [
          {
            value: employee?.employeeEmploymentBasicDetails?.location
              ? employee.employeeEmploymentBasicDetails.location
              : '',
            disabled: true
          }
        ],
        employeeEmploymentBasicDetails: [
          {
            value: employee?.employeeEmploymentBasicDetails ? employee.employeeEmploymentBasicDetails : null,
            disabled: true
          }
        ],
        employeeId: [employee?.id ? employee.id : null]
      })

      this.initialEmploymentBasicData = { ...this.employmentBasicDataForm.getRawValue() }

      this.statutoryDetailsForm = this.formBuilder.group({
        havePfAccount: [
          {
            value: employee?.employeeStatutoryDetails?.havePfAccount
              ? employee.employeeStatutoryDetails.havePfAccount
              : '',
            disabled: true
          },
          Validators.required
        ],
        pfNumber: [
          {
            value: employee?.employeeStatutoryDetails?.pfNumber ? employee.employeeStatutoryDetails.pfNumber : '',
            disabled: true
          },
          [(Validators.pattern('^[a-zA-Z0-9]+$'), Validators.minLength(22), Validators.maxLength(22))]
        ],
        uanNumber: [
          {
            value: employee?.employeeStatutoryDetails?.uanNumber ? employee.employeeStatutoryDetails.uanNumber : '',
            disabled: true
          },
          [Validators.pattern('^[0-9]*$'), Validators.minLength(12), Validators.maxLength(12)]
        ],
        /*haveEsiAccount: [
          employee?.employeeStatutoryDetails?.haveEsiAccount ? employee.employeeStatutoryDetails.haveEsiAccount : '',
          Validators.required
        ],
        esiNumber: [
          employee?.employeeStatutoryDetails?.esiNumber ? employee.employeeStatutoryDetails.esiNumber : '',
          [Validators.pattern('^[0-9]*$'), Validators.minLength(15), Validators.maxLength(15)]
        ],
        lwfApplicability: [
          employee?.employeeStatutoryDetails?.lwfApplicability
            ? employee.employeeStatutoryDetails.lwfApplicability
            : '',
          Validators.required
        ],
        lwfNumber: [
          employee?.employeeStatutoryDetails?.lwfNumber ? employee.employeeStatutoryDetails.lwfNumber : '',
          Validators.pattern('^[0-9]*$')
        ],
        nomineeName: [
          employee?.employeeStatutoryDetails?.nomineeName ? employee.employeeStatutoryDetails.nomineeName : '',
          [Validators.pattern('^[a-zA-Z ]*$'), Validators.minLength(5), Validators.maxLength(30)]
        ],
        nomineeRelationship: [
          employee?.employeeStatutoryDetails?.nomineeRelationship
            ? employee.employeeStatutoryDetails.nomineeRelationship
            : ''
        ],
        nomineeDob: [
          employee?.employeeStatutoryDetails?.nomineeDob
            ? this.datePipe.transform(new Date(employee.employeeStatutoryDetails.nomineeDob), 'yyyy-MM-dd', GlobalConstants.IST_OFFSET)
            : '',
          futureDateValidator()
        ],*/
        pan: [
          {
            value: employee?.employeeStatutoryDetails?.pan ? employee.employeeStatutoryDetails.pan : '',
            disabled: true
          },
          [
            (Validators.required,
            Validators.pattern('[A-Z]{3}[P]{1}[A-Z]{1}[0-9]{4}[A-Z]{1}'),
            Validators.minLength(10),
            Validators.maxLength(10))
          ]
        ],
        aadhaar: [
          {
            value: employee?.employeeStatutoryDetails?.aadhaar
              ? this.getMaskedAadhaar(employee.employeeStatutoryDetails.aadhaar)
              : '',
            disabled: true
          }
        ],
        employeeStatutoryDetails: [employee?.employeeStatutoryDetails ? employee.employeeStatutoryDetails : null],
        employeeId: [employee?.id ? employee.id : null]
      })

      this.initialStatutoryDetails = { ...this.statutoryDetailsForm.getRawValue() }
      this.triggerYesNoRadioChange()
    })
  }

  populateOptions() {
    let lookupValues: string[] = [
      GlobalConstants.OPTION_TYPE.MARITAL_TYPE,
      GlobalConstants.OPTION_TYPE.GENDER,
      GlobalConstants.OPTION_TYPE.NATIONALITY,
      GlobalConstants.OPTION_TYPE.CITY,
      GlobalConstants.OPTION_TYPE.STATE,
      GlobalConstants.OPTION_TYPE.COUNTRY,
      GlobalConstants.OPTION_TYPE.EMPLOYMENT_DEPARTMENT,
      GlobalConstants.OPTION_TYPE.EMPLOYMENT_BRANCH,
      GlobalConstants.OPTION_TYPE.EMPLOYMENT_CATEGORY,
      GlobalConstants.OPTION_TYPE.EMPLOYMENT_LOCATION,
      GlobalConstants.OPTION_TYPE.YES_NO,
      GlobalConstants.OPTION_TYPE.RELATIONSHIP_TYPE
    ]
    this.optionsService.getOptionsForType(lookupValues).subscribe({
      next: (responses) => {
        if (responses) {
          this.maritalStatuses = responses[GlobalConstants.OPTION_TYPE.MARITAL_TYPE]
          this.genders = responses[GlobalConstants.OPTION_TYPE.GENDER]
          this.nationalities = responses[GlobalConstants.OPTION_TYPE.NATIONALITY]
          this.cities = responses[GlobalConstants.OPTION_TYPE.CITY]
          this.states = responses[GlobalConstants.OPTION_TYPE.STATE]
          this.departments = responses[GlobalConstants.OPTION_TYPE.EMPLOYMENT_DEPARTMENT]
          this.branches = responses[GlobalConstants.OPTION_TYPE.EMPLOYMENT_BRANCH]
          this.categories = responses[GlobalConstants.OPTION_TYPE.EMPLOYMENT_CATEGORY]
          this.locations = responses[GlobalConstants.OPTION_TYPE.EMPLOYMENT_LOCATION]
          this.responses = responses[GlobalConstants.OPTION_TYPE.YES_NO]
          this.relationships = responses[GlobalConstants.OPTION_TYPE.RELATIONSHIP_TYPE]
        }
      }
    })
  }

  buildEmployeePayloadForContactData(employeeFormData) {
    let address: AddressAPIModel

    if (
      !employeeFormData ||
      (!employeeFormData.address &&
        !employeeFormData.doorNo &&
        !employeeFormData.streetName &&
        !employeeFormData.areaName &&
        !employeeFormData.country &&
        !employeeFormData.state &&
        !employeeFormData.city &&
        !employeeFormData.pinCode)
    ) {
      address = null
    } else {
      const formDataAddress = employeeFormData?.address ? { ...employeeFormData.address } : {}

      address = {
        ...formDataAddress,
        id: employeeFormData?.address?.id || null,
        address1: employeeFormData.doorNo || formDataAddress?.address1 || null,
        address2: employeeFormData.streetName || formDataAddress?.address2 || null,
        address3: employeeFormData.areaName || formDataAddress?.address3 || null,
        state: employeeFormData.state || formDataAddress?.state || null,
        city: employeeFormData.city || formDataAddress?.city || null,
        country: employeeFormData.country || formDataAddress?.country || null,
        pin: employeeFormData.pinCode || formDataAddress?.pin || null
      }
    }

    const formattedPrimaryMobileNo = employeeFormData?.primaryMobileNumber
      ? `${employeeFormData.primaryCountryCode}-${employeeFormData.primaryMobileNumber}`
      : ''
    const formattedSecondaryMobileNo = employeeFormData?.secondaryMobileNumber
      ? `${employeeFormData.secondaryCountryCode}-${employeeFormData.secondaryMobileNumber}`
      : ''

    this.employeeContactDataForm.patchValue({
      address: address
    })
    return {
      ...this.employeeContactDataForm.getRawValue(),
      mobileNumber: formattedPrimaryMobileNo,
      alternateMobileNumber: formattedSecondaryMobileNo
    }
  }

  buildEmployeePayloadForEmploymentBasicData(employmentBasicFormData) {
    let employmentBasicDetails: EmploymentBasicDetailsAPIModel

    if (
      !employmentBasicFormData ||
      (!employmentBasicFormData.employeeEmploymentBasicDetails &&
        !employmentBasicFormData.departmentName &&
        !employmentBasicFormData.branch &&
        !employmentBasicFormData.designation &&
        !employmentBasicFormData.category &&
        !employmentBasicFormData.location)
    ) {
      employmentBasicDetails = null
    } else {
      const formDataEmploymentBasicDetails = employmentBasicFormData?.employeeEmploymentBasicDetails
        ? { ...employmentBasicFormData.employeeEmploymentBasicDetails }
        : {}

      employmentBasicDetails = {
        ...formDataEmploymentBasicDetails,
        id: employmentBasicFormData?.employeeEmploymentBasicDetails?.id || null,
        departmentName:
          employmentBasicFormData?.departmentName || formDataEmploymentBasicDetails?.departmentName || null,
        branch: employmentBasicFormData?.branch || formDataEmploymentBasicDetails?.branch || null,
        designation: employmentBasicFormData?.designation || formDataEmploymentBasicDetails?.designation || null,
        category: employmentBasicFormData?.category || formDataEmploymentBasicDetails?.category || null,
        location: employmentBasicFormData?.location || formDataEmploymentBasicDetails?.location || null,
        employeeId: employmentBasicFormData?.employeeId || formDataEmploymentBasicDetails?.employeeId || null
      }
    }

    this.employmentBasicDataForm.patchValue({ employeeEmploymentBasicDetails: employmentBasicDetails })
    return this.employmentBasicDataForm.getRawValue()
  }

  buildEmployeePayloadForStatutoryDetails(statutoryDetailsFormData) {
    let statutoryDetails: StatutoryDetailsAPIModel

    if (
      !statutoryDetailsFormData ||
      (!statutoryDetailsFormData.employeeStatutoryDetails &&
        !statutoryDetailsFormData.havePfAccount &&
        !statutoryDetailsFormData.pfNumber &&
        !statutoryDetailsFormData.uanNumber &&
        /*!statutoryDetailsFormData.haveEsiAccount &&
        !statutoryDetailsFormData.esiNumber &&
        !statutoryDetailsFormData.lwfApplicability &&
        !statutoryDetailsFormData.lwfNumber &&
        !statutoryDetailsFormData.nomineeName &&
        !statutoryDetailsFormData.nomineeRelationship &&
        !statutoryDetailsFormData.nomineeDob &&*/
        !statutoryDetailsFormData.pan &&
        !statutoryDetailsFormData.aadhaar)
    ) {
      statutoryDetails = null
    } else {
      const formDataStatutoryDetails = statutoryDetailsFormData?.employeeStatutoryDetails
        ? { ...statutoryDetailsFormData.employeeStatutoryDetails }
        : {}

      statutoryDetails = {
        ...formDataStatutoryDetails,
        id: statutoryDetailsFormData?.employeeEmploymentBasicDetails?.id || null,
        havePfAccount:
          statutoryDetailsFormData?.havePfAccount ||
          formDataStatutoryDetails?.employeeStatutoryDetails?.havePfAccount ||
          null,
        pfNumber:
          statutoryDetailsFormData?.pfNumber || formDataStatutoryDetails?.employeeStatutoryDetails?.pfNumber || null,
        uanNumber:
          statutoryDetailsFormData?.uanNumber || formDataStatutoryDetails?.employeeStatutoryDetails?.uanNumber || null,
        /*haveEsiAccount:
          statutoryDetailsFormData?.haveEsiAccount ||
          formDataStatutoryDetails?.employeeStatutoryDetails?.haveEsiAccount ||
          null,
        esiNumber:
          statutoryDetailsFormData?.esiNumber || formDataStatutoryDetails?.employeeStatutoryDetails?.esiNumber || null,
        lwfApplicability:
          statutoryDetailsFormData?.lwfApplicability ||
          formDataStatutoryDetails?.employeeStatutoryDetails?.lwfApplicability ||
          null,
        lwfNumber:
          statutoryDetailsFormData?.lwfNumber || formDataStatutoryDetails?.employeeStatutoryDetails?.lwfNumber || null,
        nomineeName:
          statutoryDetailsFormData?.nomineeName ||
          formDataStatutoryDetails?.employeeStatutoryDetails?.nomineeName ||
          null,
        nomineeRelationship:
          statutoryDetailsFormData?.nomineeRelationship ||
          formDataStatutoryDetails?.employeeStatutoryDetails?.nomineeRelationship ||
          null,
        nomineeDob:
          statutoryDetailsFormData?.nomineeDob ||
          formDataStatutoryDetails?.employeeStatutoryDetails?.nomineeDob ||
          null,*/
        pan: statutoryDetailsFormData?.pan || formDataStatutoryDetails?.employeeStatutoryDetails?.pan || null,
        aadhaar:
          statutoryDetailsFormData?.aadhaar || formDataStatutoryDetails?.employeeStatutoryDetails?.aadhaar || null,
        employeeId: statutoryDetailsFormData?.employeeId || statutoryDetailsFormData?.employeeId || null
      }
    }

    this.statutoryDetailsForm.patchValue({
      employeeStatutoryDetails: statutoryDetails,
      pfNumber:
        statutoryDetails && statutoryDetails.havePfAccount !== null && statutoryDetails.havePfAccount === 'No'
          ? null
          : statutoryDetails?.pfNumber,
      uanNumber:
        statutoryDetails && statutoryDetails.havePfAccount !== null && statutoryDetails.havePfAccount === 'No'
          ? null
          : statutoryDetails?.uanNumber
    })
    return this.statutoryDetailsForm.getRawValue()
  }

  reset(formGroup: FormGroup, initialValue: any) {
    formGroup.reset(initialValue)

    if (formGroup === this.statutoryDetailsForm) {
      this.triggerYesNoRadioChange()
    }
  }

  triggerYesNoRadioChange() {
    this.onYesNoRadioChange(this.HAVE_PF_ACC_CONTROL_NAME, [this.PF_NUMBER_CONTROL_NAME, this.UAN_NUMBER_CONTROL_NAME])
    /*this.onYesNoRadioChange(this.HAVE_ESI_ACC_CONTROL_NAME, [this.ESI_NUMBER_CONTROL_NAME])
    this.onYesNoRadioChange(this.HAVE_LWF_ACC_CONTROL_NAME, [this.LWF_NUMBER_CONTROL_NAME])*/
  }

  onYesNoRadioChange(radioBtnCtrlName: string, disableFieldsCtrlName: string[]) {
    const radioBtnControl = this.statutoryDetailsForm.controls[radioBtnCtrlName]

    if (radioBtnControl && radioBtnControl.value === 'Yes') {
      /*for (const disableFieldCtrlName of disableFieldsCtrlName) {
        const disableFieldControl = this.statutoryDetailsForm.controls[disableFieldCtrlName]
        disableFieldControl?.enable()
      }*/
    } else {
      for (const disableFieldCtrlName of disableFieldsCtrlName) {
        const disableFieldControl = this.statutoryDetailsForm.controls[disableFieldCtrlName]
        disableFieldControl?.disable()
        disableFieldControl?.setValue('')
      }
    }
  }

  getMaskedAadhaar(aadhaar: string): string {
    if (aadhaar && aadhaar.trim() !== '' && aadhaar.length === 12) {
      return 'XXXXXXXX' + aadhaar.slice(-4)
    }
    return 'NA'
  }

  ngOnDestroy() {
    this.employeeDataSubscription?.unsubscribe()
  }
}
